/**
 * Min-Quantity Promo — theme integration
 * ──────────────────────────────────────
 * Surfaces an active min_quantity_percentage campaign on PDPs (and
 * inside the vendor-cart card) so practitioners discover the offer
 * BEFORE committing to a 12+ unit purchase.
 *
 * Source of truth: GET /api/storefront/active-promos on BR Connect
 * returns the live min_quantity_percentage campaigns with
 * eligibleProductIds + minQuantity + percentage. Cart state from
 * /cart.js. cart:updated pubsub event triggers re-renders.
 *
 * Pure read-only — Shopify enforces the threshold at checkout via the
 * native discountCodeBasic / discountAutomaticBasic discount node we
 * created in BR Connect's launch flow. This script's only job is to
 * inform the customer + show progress (X/N).
 *
 * Sibling of bxgy-promo.js (Marini Q2 BXGY) and bundle-promo.js (MSS
 * May bundle). Each module owns its own DOM hooks (data-min-qty-promo*)
 * so they don't collide; common API path + tiny utilities are
 * duplicated by design — the alternative (a shared promo-runtime
 * module) would force a refactor of bxgy-promo.js, which is in
 * production for Marini Q2 and validated. Once all three campaigns are
 * live for a quarter we can DRY them up.
 */
(function () {
  'use strict';

  var BR_CONNECT_BASE =
    window.__beautyRepConnectBase || 'https://beautyrepconnect.com';
  var ACTIVE_PROMOS_PATH = '/api/storefront/active-promos';
  var SOURCE_LABEL = 'min-qty-promo';

  /* ────────────────────────────────────────
     Tiny utilities (intentionally duplicated from bxgy-promo.js — see header)
  ──────────────────────────────────────── */

  function gidToNumericId(gid) {
    if (!gid || typeof gid !== 'string') return null;
    var m = gid.match(/(\d+)$/);
    return m ? m[1] : null;
  }

  function fetchJsonResilient(url, init, retriesLeft) {
    if (typeof retriesLeft !== 'number') retriesLeft = 2;
    var backoffs = [1500, 3000];
    var attempt = 2 - retriesLeft;
    return fetch(url, init)
      .then(function (res) {
        if (res.status === 429 && retriesLeft > 0) {
          var wait = backoffs[Math.min(attempt, backoffs.length - 1)];
          return new Promise(function (resolve) { setTimeout(resolve, wait); })
            .then(function () { return fetchJsonResilient(url, init, retriesLeft - 1); });
        }
        if (!res.ok) throw new Error(url + ' → ' + res.status);
        return res.json();
      })
      .catch(function (err) {
        if (retriesLeft > 0) {
          var wait = backoffs[Math.min(attempt, backoffs.length - 1)];
          return new Promise(function (resolve) { setTimeout(resolve, wait); })
            .then(function () { return fetchJsonResilient(url, init, retriesLeft - 1); });
        }
        throw err;
      });
  }

  function debounce(fn, ms) {
    var t = null;
    return function () {
      var args = arguments;
      var ctx = this;
      if (t) clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  /* ────────────────────────────────────────
     State
  ──────────────────────────────────────── */

  // One promo per page render — if multiple min_quantity_percentage
  // campaigns are active and the current product is eligible for several,
  // we pick the highest-percentage one (best deal for the customer).
  var state = {
    promo: null,           // { campaignId, title, minQuantity, percentage, eligibleNumericIds: Set<string>, endsAt }
    cartLines: [],         // /cart.js items[] cached
    inflightCartFetch: null,
    productCards: {},      // numericId → ProductCard (from /active-promos response)
  };

  /* ────────────────────────────────────────
     Promo selection
  ──────────────────────────────────────── */

  function pickBestMinQtyPromo(allPromos, productNumericId, customerBrand) {
    if (!Array.isArray(allPromos)) return null;
    var matching = allPromos.filter(function (p) {
      if (p.type !== 'min_quantity_percentage') return false;
      // Brand gating: if the promo is segment-gated (audience.brand set)
      // and we know the customer's brand, only show matching. If we
      // don't know the customer's brand (anon visitor), show all — the
      // discount won't actually apply but we can still tease.
      if (p.audience && p.audience.brand && customerBrand && p.audience.brand !== customerBrand) {
        return false;
      }
      var eligibleIds = (p.eligibleProductIds || [])
        .map(gidToNumericId)
        .filter(Boolean);
      return eligibleIds.indexOf(String(productNumericId)) !== -1;
    });
    if (!matching.length) return null;
    matching.sort(function (a, b) { return (b.percentage || 0) - (a.percentage || 0); });
    var best = matching[0];
    var eligibleNumericIds = new Set();
    var eligibleNumericIdList = [];
    (best.eligibleProductIds || []).forEach(function (gid) {
      var n = gidToNumericId(gid);
      if (n) {
        eligibleNumericIds.add(n);
        eligibleNumericIdList.push(n);
      }
    });
    return {
      campaignId: best.campaignId,
      title: best.title,
      minQuantity: best.minQuantity,
      percentage: best.percentage,
      eligibleNumericIds: eligibleNumericIds,
      eligibleNumericIdList: eligibleNumericIdList,
      endsAt: best.endsAt,
    };
  }

  /**
   * Pick a min-qty promo for a cart context (no PDP product). Used by
   * the cart-card + drawer renders. Picks the highest-% promo whose
   * eligibles overlap any cart line, scoped to the customer's brand.
   */
  function pickBestMinQtyPromoForCart(allPromos, customerBrand, cartLines) {
    if (!Array.isArray(allPromos)) return null;
    var cartProductIds = new Set();
    (cartLines || []).forEach(function (l) { cartProductIds.add(String(l.product_id)); });
    var matching = allPromos.filter(function (p) {
      if (p.type !== 'min_quantity_percentage') return false;
      if (p.audience && p.audience.brand && customerBrand && p.audience.brand !== customerBrand) {
        return false;
      }
      var eligibleIds = (p.eligibleProductIds || [])
        .map(gidToNumericId)
        .filter(Boolean);
      return eligibleIds.some(function (id) { return cartProductIds.has(id); });
    });
    if (!matching.length) return null;
    matching.sort(function (a, b) { return (b.percentage || 0) - (a.percentage || 0); });
    var best = matching[0];
    var eligibleNumericIds = new Set();
    var eligibleNumericIdList = [];
    (best.eligibleProductIds || []).forEach(function (gid) {
      var n = gidToNumericId(gid);
      if (n) {
        eligibleNumericIds.add(n);
        eligibleNumericIdList.push(n);
      }
    });
    return {
      campaignId: best.campaignId,
      title: best.title,
      minQuantity: best.minQuantity,
      percentage: best.percentage,
      eligibleNumericIds: eligibleNumericIds,
      eligibleNumericIdList: eligibleNumericIdList,
      endsAt: best.endsAt,
    };
  }

  /* ────────────────────────────────────────
     Cart fetch + count
  ──────────────────────────────────────── */

  function fetchCart() {
    if (state.inflightCartFetch) return state.inflightCartFetch;
    state.inflightCartFetch = fetchJsonResilient('/cart.js')
      .then(function (cart) {
        state.cartLines = (cart && cart.items) || [];
        state.inflightCartFetch = null;
        return cart;
      })
      .catch(function (err) {
        state.inflightCartFetch = null;
        // Don't throw — let render show "—" rather than break the PDP.
        console.warn('[' + SOURCE_LABEL + '] cart fetch failed:', err);
        return { items: state.cartLines };
      });
    return state.inflightCartFetch;
  }

  function countEligibleInCart(eligibleNumericIds) {
    var total = 0;
    for (var i = 0; i < state.cartLines.length; i++) {
      var line = state.cartLines[i];
      if (eligibleNumericIds.has(String(line.product_id))) {
        total += line.quantity || 0;
      }
    }
    return total;
  }

  /* ────────────────────────────────────────
     Render — PDP banner
  ──────────────────────────────────────── */

  function renderPdp() {
    var el = document.querySelector('[data-min-qty-promo-pdp]');
    if (!el) return;
    if (!state.promo) {
      el.classList.add('hidden');
      return;
    }
    var promo = state.promo;
    // Gate by PDP product membership: the promo may have been chosen by
    // the cart fallback (state.promo set via pickBestMinQtyPromoForCart
    // because some cart line is eligible). Without this check the PDP
    // banner would render on any product page in the same shop while
    // the cart has any eligible item — even on products NOT in the
    // promo's eligibles. Banner must reflect *this product's* eligibility.
    var pdpProductId = el.getAttribute('data-product-id');
    if (pdpProductId && !promo.eligibleNumericIds.has(String(pdpProductId))) {
      el.classList.add('hidden');
      return;
    }
    // Use the actual cart count, not the PDP qty input projection.
    // The previous "projected" math (cart + PDP qty input) made the
    // banner read "Currently 1/12 in cart" when nothing was in the
    // cart yet — the 1 was just the qty selector default. Honest
    // labelling beats a clever projection here.
    var inCartQty = countEligibleInCart(promo.eligibleNumericIds);
    var qualifies = inCartQty >= promo.minQuantity;
    var pctLabel = Math.round((promo.percentage || 0) * 100) + '%';
    el.classList.remove('hidden');

    var below = el.querySelector('[data-min-qty-below]');
    var qualified = el.querySelector('[data-min-qty-qualified]');
    if (qualifies) {
      if (below) below.classList.add('hidden');
      if (qualified) qualified.classList.remove('hidden');
      var savingsLabel = qualified && qualified.querySelector('[data-min-qty-pct]');
      if (savingsLabel) savingsLabel.textContent = pctLabel;
      var totalLabel = qualified && qualified.querySelector('[data-min-qty-current]');
      if (totalLabel) totalLabel.textContent = String(inCartQty);
    } else {
      if (below) below.classList.remove('hidden');
      if (qualified) qualified.classList.add('hidden');
      var needSpan = below && below.querySelector('[data-min-qty-need]');
      var pctSpan = below && below.querySelector('[data-min-qty-pct]');
      var currentSpan = below && below.querySelector('[data-min-qty-current]');
      var thresholdSpan = below && below.querySelector('[data-min-qty-threshold]');
      if (needSpan) needSpan.textContent = String(promo.minQuantity - inCartQty);
      if (pctSpan) pctSpan.textContent = pctLabel;
      if (currentSpan) currentSpan.textContent = String(inCartQty);
      if (thresholdSpan) thresholdSpan.textContent = String(promo.minQuantity);
    }
    var bar = el.querySelector('[data-min-qty-bar]');
    if (bar) {
      var pct = Math.max(
        0,
        Math.min(100, Math.round((inCartQty / promo.minQuantity) * 100)),
      );
      bar.style.width = pct + '%';
    }
    // "See eligible products" trigger — un-hide once we have a promo
    // resolved. The trigger lives in BOTH below + qualified states so
    // practitioners can still browse the eligible set even after they
    // pass the threshold.
    var triggers = el.querySelectorAll('[data-min-qty-see-products]');
    triggers.forEach(function (t) { t.classList.remove('hidden'); });
  }

  /* ────────────────────────────────────────
     Render — vendor cart card
  ──────────────────────────────────────── */

  /**
   * Count eligibles in cart, scoped to a specific vendor (cart card +
   * drawer use this so each card only "sees" its own vendor lines).
   */
  function countEligibleInCartForVendor(eligibleNumericIds, vendor) {
    var total = 0;
    for (var i = 0; i < state.cartLines.length; i++) {
      var line = state.cartLines[i];
      if (vendor && line.vendor !== vendor) continue;
      if (eligibleNumericIds.has(String(line.product_id))) {
        total += line.quantity || 0;
      }
    }
    return total;
  }

  function renderCard() {
    // Per-vendor card targets are scoped by data-vendor attribute on the
    // promo card element — this lets the same script feed multiple vendor
    // cards on /carts (multi-vendor mix editor) without leaking state.
    //
    // Per-card visibility rule: only un-hide if THIS vendor has at least
    // one eligible product in cart. Otherwise the banner would show on
    // unrelated brand carts (e.g. on a Babor cart while Marini has the
    // active min-qty promo). The brand-side gating already filtered
    // promos to the customer's primary brand, but a customer can be
    // Marini-primary and still have a Babor cart open — we don't want
    // the Marini banner showing on the Babor card.
    var els = document.querySelectorAll('[data-min-qty-promo-card]');
    if (!els.length || !state.promo) return;
    var promo = state.promo;
    var pctLabel = Math.round((promo.percentage || 0) * 100) + '%';
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var vendor = el.getAttribute('data-vendor') || null;
      var inCartQty = countEligibleInCartForVendor(promo.eligibleNumericIds, vendor);
      if (inCartQty <= 0) {
        // Nothing in this vendor's cart matches — hide banner on this
        // card (it's irrelevant context, like the Skin Zyme/SCMS bug).
        el.classList.add('hidden');
        continue;
      }
      var qualifies = inCartQty >= promo.minQuantity;
      el.classList.remove('hidden');
      var below = el.querySelector('[data-min-qty-below]');
      var qualified = el.querySelector('[data-min-qty-qualified]');
      if (qualifies) {
        if (below) below.classList.add('hidden');
        if (qualified) qualified.classList.remove('hidden');
        var pctSpan = qualified && qualified.querySelector('[data-min-qty-pct]');
        var currentSpan = qualified && qualified.querySelector('[data-min-qty-current]');
        if (pctSpan) pctSpan.textContent = pctLabel;
        if (currentSpan) currentSpan.textContent = String(inCartQty);
      } else {
        if (below) below.classList.remove('hidden');
        if (qualified) qualified.classList.add('hidden');
        var needSpan = below && below.querySelector('[data-min-qty-need]');
        var pctSpan2 = below && below.querySelector('[data-min-qty-pct]');
        var currentSpan2 = below && below.querySelector('[data-min-qty-current]');
        var thresholdSpan = below && below.querySelector('[data-min-qty-threshold]');
        if (needSpan) needSpan.textContent = String(promo.minQuantity - inCartQty);
        if (pctSpan2) pctSpan2.textContent = pctLabel;
        if (currentSpan2) currentSpan2.textContent = String(inCartQty);
        if (thresholdSpan) thresholdSpan.textContent = String(promo.minQuantity);
      }
      var bar = el.querySelector('[data-min-qty-bar]');
      if (bar) {
        var pct = Math.max(
          0,
          Math.min(100, Math.round((inCartQty / promo.minQuantity) * 100)),
        );
        bar.style.width = pct + '%';
      }
      // "See eligible products" trigger surfaces in cart cards too.
      var triggers = el.querySelectorAll('[data-min-qty-see-products]');
      triggers.forEach(function (t) { t.classList.remove('hidden'); });
    }
  }

  /* ────────────────────────────────────────
     Render — orchestrator
  ──────────────────────────────────────── */

  function renderAll() {
    renderPdp();
    renderCard();
  }

  var debouncedRender = debounce(function () {
    fetchCart().then(renderAll);
  }, 150);

  /* ────────────────────────────────────────
     Bootstrap
  ──────────────────────────────────────── */

  function getCustomerBrand() {
    // Theme injects this from {{ customer.metafields.custom.source_brand }}
    // (Liquid) into a top-of-document <meta>. Falls back to null for
    // anonymous visitors.
    var meta = document.querySelector('meta[name="br-customer-brand"]');
    return meta ? (meta.getAttribute('content') || '').toLowerCase() : null;
  }

  function getProductNumericId() {
    var pdp = document.querySelector('[data-min-qty-promo-pdp]');
    if (pdp) {
      var pid = pdp.getAttribute('data-product-id');
      if (pid) return pid;
    }
    // Fallback: many PDPs expose product.id on a body class or as a meta tag.
    var meta = document.querySelector('meta[name="shopify-product-id"]');
    return meta ? meta.getAttribute('content') : null;
  }

  function init() {
    var pdpEl = document.querySelector('[data-min-qty-promo-pdp]');
    var cardEls = document.querySelectorAll('[data-min-qty-promo-card]');
    if (!pdpEl && !cardEls.length) return; // nothing to render

    // Preview mode: include drafts.
    var includeDraft =
      window.Shopify && window.Shopify.designMode
        ? '?include_draft=1'
        : (location.search.indexOf('preview_theme_id=') !== -1 ? '?include_draft=1' : '');

    // PromoCompat (assets/promo-compat.js) decides which campaigns "win"
    // when productDiscounts:false promos overlap. Await its decision and
    // filter `data.promos` so the picker functions never see overruled
    // campaigns. Fail-open if the helper is missing.
    var compatReady = (window.PromoCompat && window.PromoCompat.ready) || Promise.resolve(null);
    Promise.all([
      fetchJsonResilient(BR_CONNECT_BASE + ACTIVE_PROMOS_PATH + includeDraft),
      compatReady,
    ])
      .then(function (results) {
        var data = results[0];
        var compat = results[1];
        var customerBrand = getCustomerBrand();
        var productId = getProductNumericId();
        // Productcards map (richer than productHandles — image, price,
        // variantId for the modal). productHandles is still in the
        // response for backward compat with the bundle-promo CTA flow.
        state.productCards = (data && data.productCards) || {};
        var availablePromos = (data && data.promos || []).filter(function (p) {
          if (!compat || !compat.byId || !compat.byId[p.campaignId]) return true;
          return compat.byId[p.campaignId].applicable;
        });
        // PDP: pick a promo that includes this product. Card mode: pick
        // the first matching promo regardless of product (we'll show
        // progress against the cart's eligible items).
        if (productId) {
          state.promo = pickBestMinQtyPromo(availablePromos, productId, customerBrand);
        }
        // Always also resolve the cart-mode promo (so the cart-card
        // banner has something to render even when the current PDP
        // product isn't in this promo's eligibles).
        return fetchCart().then(function () {
          if (!state.promo && cardEls.length) {
            state.promo = pickBestMinQtyPromoForCart(
              availablePromos, customerBrand, state.cartLines,
            );
          }
          if (!state.promo) {
            if (pdpEl) pdpEl.classList.add('hidden');
            return;
          }
          renderAll();
        });
      })
      .catch(function (err) {
        console.warn('[' + SOURCE_LABEL + '] init failed:', err);
        if (pdpEl) pdpEl.classList.add('hidden');
      });

    // Reactive listeners.
    // No PDP-qty-input listener: the banner now reflects the real cart
    // count, not the projected (cart + PDP qty selector). The qty
    // selector only matters once the user clicks Add to Cart, at which
    // point cart:updated fires and we re-render then.
    document.addEventListener('cart:updated', debouncedRender);
    // Tab refocus — cover the case where the user added items in
    // another tab and came back.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') debouncedRender();
    });

    // "See eligible products" trigger — opens the promo-products-modal
    // with the resolved campaign's eligible product cards. Delegated so
    // both PDP and card surfaces share one binding (currently only PDP
    // renders the trigger; cards may follow).
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-min-qty-see-products]');
      if (!btn) return;
      e.preventDefault();
      openModal();
    });
  }

  /* ────────────────────────────────────────
     Modal
  ──────────────────────────────────────── */

  function openModal() {
    if (!state.promo || !window.PromoProductsModal) return;
    var promo = state.promo;
    var pctLabel = Math.round((promo.percentage || 0) * 100) + '%';
    // Build product cards from /active-promos.productCards. Order
    // mirrors the campaign's eligibleProductIds — predictable for the
    // merchant who set the order intentionally.
    var products = promo.eligibleNumericIdList
      .map(function (id) { return state.productCards[id] || null; })
      .filter(Boolean);
    var currentNumericId = getProductNumericId();
    var inCartCount = countEligibleInCart(promo.eligibleNumericIds);
    var need = Math.max(0, promo.minQuantity - inCartCount);
    var sub;
    if (need > 0) {
      sub =
        'Add ' + need + ' more from this list (any combination) to unlock ' +
        pctLabel + ' off all qualifying lines. ' +
        inCartCount + '/' + promo.minQuantity + ' currently in cart.';
    } else {
      sub =
        'You qualify for ' + pctLabel + ' off all qualifying lines (' +
        inCartCount + '/' + promo.minQuantity + '+). ' +
        'Browse the set to top up if you want.';
    }
    window.PromoProductsModal.open({
      eyebrow: 'Mix & match · ' + pctLabel + ' off',
      title: promo.title,
      sub: sub,
      footnote:
        promo.minQuantity +
        '+ units across this list applies the discount automatically at checkout. ' +
        'No code needed.',
      products: products,
      currentNumericId: currentNumericId,
      mode: 'min-qty',
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
